/**
 * DataTable — reusable list table with:
 *  - sticky header (scrolls within a bounded container)
 *  - density toggle (comfortable / compact)
 *  - inline per-column filters (client-side, toggleable)
 *  - loading + empty states
 *
 * Columns: { key, header, align?, render?(row), accessor?(row), filterable?, filterType?, filterOptions? }
 */
import React, { useState, useMemo } from 'react';
import { Loader2, AlertCircle, Filter, Rows3, Rows4, X } from 'lucide-react';
import { Input } from '../ui/input';

const DataTable = ({
  columns,
  data = [],
  loading = false,
  emptyText = 'No records found.',
  emptyHint,
  rowKey,
  maxHeight = 'calc(100vh - 340px)',
  testid = 'data-table',
  initialDense = false,
  rowProps,
}) => {
  // Density preference persists per-table (keyed by testid) across navigation.
  const densityKey = `dataTable.density.${testid}`;
  const [dense, setDense] = useState(() => {
    try {
      const stored = localStorage.getItem(densityKey);
      return stored === null ? initialDense : stored === 'compact';
    } catch {
      return initialDense;
    }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [colFilters, setColFilters] = useState({});

  const toggleDense = () =>
    setDense((d) => {
      const next = !d;
      try { localStorage.setItem(densityKey, next ? 'compact' : 'comfortable'); } catch (_) { /* ignore */ }
      return next;
    });

  const setFilter = (key, val) => setColFilters((prev) => ({ ...prev, [key]: val }));
  const clearFilters = () => setColFilters({});

  const filtered = useMemo(() => {
    const active = Object.entries(colFilters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (!active.length) return data;
    return data.filter((row) =>
      active.every(([key, val]) => {
        const col = columns.find((c) => c.key === key);
        const cell = col?.accessor ? col.accessor(row) : row[key];
        return String(cell ?? '').toLowerCase().includes(String(val).toLowerCase());
      }),
    );
  }, [data, colFilters, columns]);

  const cellPad = dense ? 'py-1.5 px-3 text-xs' : 'py-3 px-4 text-sm';
  const headPad = dense ? 'py-2 px-3 text-xs' : 'py-2.5 px-4 text-sm';
  const alignCls = (a) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');
  const hasFilterable = columns.some((c) => c.filterable);
  const activeFilterCount = Object.values(colFilters).filter((v) => v && String(v).trim() !== '').length;

  return (
    <div data-testid={testid}>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500" data-testid="data-table-count">
          {loading ? 'Loading…' : `${filtered.length} ${filtered.length === 1 ? 'record' : 'records'}`}
          {activeFilterCount > 0 && ` (filtered)`}
        </p>
        <div className="flex items-center gap-2">
          {hasFilterable && (
            <button
              onClick={() => setShowFilters((s) => !s)}
              data-testid="data-table-filter-toggle"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                showFilters ? 'bg-accent text-accent-foreground border-primary/30' : 'bg-white text-gray-600 border-border hover:bg-gray-50'
              }`}
            >
              <Filter className="w-3.5 h-3.5" strokeWidth={1.5} />
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          )}
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              data-testid="data-table-clear-filters"
              className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-gray-800"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Clear
            </button>
          )}
          <button
            onClick={toggleDense}
            data-testid="data-table-density-toggle"
            title={dense ? 'Comfortable rows' : 'Compact rows'}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border bg-white text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {dense ? <Rows3 className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Rows4 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            {dense ? 'Comfortable' : 'Compact'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 border border-border rounded-md bg-white">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-border rounded-md bg-white">
          <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">{emptyText}</p>
          {emptyHint && <p className="text-sm text-gray-400 mt-1">{emptyHint}</p>}
        </div>
      ) : (
        <div className="overflow-auto rounded-md border border-border bg-white" style={{ maxHeight }}>
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`${headPad} ${alignCls(col.align)} font-semibold text-gray-700 bg-gray-50 border-b border-border whitespace-nowrap`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
              {showFilters && (
                <tr data-testid="data-table-filter-row">
                  {columns.map((col) => (
                    <th key={col.key} className="px-3 py-2 bg-gray-50 border-b border-border align-top">
                      {col.filterable ? (
                        col.filterType === 'select' ? (
                          <select
                            value={colFilters[col.key] || ''}
                            onChange={(e) => setFilter(col.key, e.target.value)}
                            data-testid={`data-table-filter-${col.key}`}
                            className="w-full h-8 text-xs rounded-md border border-input bg-white px-2 outline-none focus:ring-2 focus:ring-ring/30"
                          >
                            <option value="">All</option>
                            {(col.filterOptions || []).map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            value={colFilters[col.key] || ''}
                            onChange={(e) => setFilter(col.key, e.target.value)}
                            placeholder="Filter…"
                            data-testid={`data-table-filter-${col.key}`}
                            className="h-8 text-xs"
                          />
                        )
                      ) : null}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {filtered.map((row, idx) => {
                const extra = rowProps ? rowProps(row) : {};
                return (
                  <tr
                    key={rowKey ? rowKey(row) : idx}
                    className="border-b border-border last:border-0 hover:bg-orange-50/40 transition-colors"
                    {...extra}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={`${cellPad} ${alignCls(col.align)} text-gray-700`}>
                        {col.render ? col.render(row) : col.accessor ? col.accessor(row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DataTable;
